import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageSquare, Mail, Copy, Link, CheckCircle2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';

function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  }
  return fallbackCopy(text);
}

function fallbackCopy(text) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try { document.execCommand('copy'); } catch (e) { /* ignore */ }
  document.body.removeChild(textarea);
  return Promise.resolve();
}

export default function InviteShareDialog({ open, onOpenChange, inviteLink, messageTemplate, loanAmount, currency }) {
  const [copied, setCopied] = useState(false);
  const amountText = formatCurrency(loanAmount, currency);

  const fullMessage = `${messageTemplate}\n\nLoan Amount: ${amountText}\n\n${inviteLink}`;

  const handleWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(fullMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`Pakka: Confirm our loan agreement (${amountText})`);
    const body = encodeURIComponent(fullMessage);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleCopy = async () => {
    await copyToClipboard(inviteLink);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyMessage = async () => {
    await copyToClipboard(fullMessage);
    toast.success('Full message copied!');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="invite-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />
            Share Invite
          </DialogTitle>
          <DialogDescription>
            Send this invite link to the borrower so they can join and confirm the loan
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Message Preview */}
          <div className="bg-muted/50 border border-border rounded-sm p-4 text-sm text-muted-foreground">
            <p className="whitespace-pre-line">{fullMessage}</p>
          </div>

          {/* Share Options */}
          <div className="grid grid-cols-1 gap-3">
            <Button
              onClick={handleWhatsApp}
              data-testid="share-whatsapp-button"
              className="w-full h-12 rounded-sm font-semibold text-xs uppercase tracking-wider"
              style={{ backgroundColor: '#25D366', color: '#fff' }}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Share via WhatsApp
            </Button>

            <Button
              onClick={handleEmail}
              data-testid="share-email-button"
              variant="outline"
              className="w-full h-12 rounded-sm font-semibold text-xs uppercase tracking-wider border-primary text-primary"
            >
              <Mail className="w-4 h-4 mr-2" />
              Share via Email
            </Button>

            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Input
                  readOnly
                  value={inviteLink}
                  className="h-11 rounded-sm pr-10 text-xs font-mono"
                  data-testid="invite-link-input"
                />
              </div>
              <Button
                onClick={handleCopy}
                data-testid="copy-link-button"
                variant="outline"
                className="h-11 px-4 rounded-sm border-primary text-primary"
              >
                {copied ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            <button
              onClick={handleCopyMessage}
              data-testid="copy-full-message-button"
              className="text-xs text-muted-foreground hover:text-foreground underline text-center"
            >
              Copy full message with link
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
